import { posthog } from '@/providers/posthog';
import { FF_RECOVER_WAF_CHALLENGES_CROSS_TAB } from '@/consts/featureFlags';
import { recordWAFTelemetry } from './telemetry';
import { WAFTelemetryOutcomeExtra, WAFTelemetryPhase } from './types';

export const WAF_CLEARED_HINT_KEY = 'wv_waf_cleared_at';
export const WAF_CLEARED_HINT_FRESHNESS_MS = 60_000;
export const WAF_BROADCAST_CHANNEL_NAME = 'weave-waf-clearance-channel';

const WAF_CLEARED_MESSAGE_TYPE = 'cleared';

let hasRecordedCrossTabUnavailable = false;

export const TestOnlyResetCrossTabUnavailableLatch = (): void => {
  hasRecordedCrossTabUnavailable = false;
};

function recordCrossTabUnavailableOnce(): void {
  if (hasRecordedCrossTabUnavailable) {
    return;
  }
  hasRecordedCrossTabUnavailable = true;
  recordWAFTelemetry({
    phase: WAFTelemetryPhase.Detected,
    outcome: WAFTelemetryOutcomeExtra.CrossTabUnavailable,
  });
}

/**
 * The client cannot read the HttpOnly, edge-set `cf_clearance` cookie, so a fresh hint is never proof this
 * tab is cleared — only a signal a sibling minted the shared cookie recently. The replay is the real proof:
 * a wrong guess just 403s and the bounded-replay loop re-solves, so a short freshness window is safe. A
 * backwards/future clock would otherwise pin a stale hint fresh forever.
 */
export function readClearedHintFresh(): boolean {
  try {
    const raw = localStorage.getItem(WAF_CLEARED_HINT_KEY);
    if (raw === null) {
      return false;
    }
    const clearedAtMs = Number(raw);
    const ageMs = Date.now() - clearedAtMs;
    return ageMs >= 0 && ageMs < WAF_CLEARED_HINT_FRESHNESS_MS;
  } catch {
    recordCrossTabUnavailableOnce();
    return false;
  }
}

export function writeClearedHint(): void {
  try {
    localStorage.setItem(WAF_CLEARED_HINT_KEY, String(Date.now()));
  } catch {
    recordCrossTabUnavailableOnce();
  }
}

export function clearClearedHint(): void {
  try {
    localStorage.removeItem(WAF_CLEARED_HINT_KEY);
  } catch {
    recordCrossTabUnavailableOnce();
  }
}

/**
 * The single recovery-hardening gate: the cross-tab hint/broadcast AND the per-tab circuit breaker both ride this
 * one flag (deliberately, so they share one kill switch). Flipping it off disables the breaker too, so despite the
 * `CrossTab` name it is not cross-tab-specific.
 */
export const isCrossTabRecoveryGateOpen = (): boolean => {
  try {
    return posthog.isFeatureEnabled(FF_RECOVER_WAF_CHALLENGES_CROSS_TAB) === true;
  } catch {
    return false;
  }
};

function isBroadcastChannelSupported(): boolean {
  return typeof BroadcastChannel !== 'undefined';
}

let broadcastChannel: BroadcastChannel | undefined;

/**
 * Opens the clearance channel, gated at call time. Call only after feature flags have settled: the gate is
 * read synchronously here, so a flag-off tab neither opens a channel nor emits any signal. Idempotent; the
 * `onSiblingCleared` callback fires when a sibling tab broadcasts a genuine solve.
 */
export function initWAFCrossTabChannel(onSiblingCleared: () => void): void {
  if (broadcastChannel !== undefined || !isCrossTabRecoveryGateOpen()) {
    return;
  }
  if (!isBroadcastChannelSupported()) {
    recordCrossTabUnavailableOnce();
    return;
  }
  try {
    const channel = new BroadcastChannel(WAF_BROADCAST_CHANNEL_NAME);
    channel.onmessage = (event: MessageEvent<{ type?: unknown } | null>) => {
      // Re-check the flag on receive too: the channel outlives a mid-session flag flip, and a flag-off tab
      // must ignore a flag-on sibling's broadcast.
      if (event.data?.type !== WAF_CLEARED_MESSAGE_TYPE || !isCrossTabRecoveryGateOpen()) {
        return;
      }
      onSiblingCleared();
    };
    broadcastChannel = channel;
  } catch {
    broadcastChannel = undefined;
    recordCrossTabUnavailableOnce();
  }
}

export function broadcastCleared(): void {
  if (!isCrossTabRecoveryGateOpen() || broadcastChannel === undefined) {
    return;
  }
  try {
    broadcastChannel.postMessage({ type: WAF_CLEARED_MESSAGE_TYPE });
  } catch {
    recordCrossTabUnavailableOnce();
  }
}

export function TestOnlyResetWAFCrossTabChannel(): void {
  try {
    broadcastChannel?.close();
  } finally {
    broadcastChannel = undefined;
  }
}
